import Marketplace from '@spike/marketplace-model';
import { BusinessHours, createEmptyWeek, Period, Week } from '@spike/model';
import { DropdownSelectOption } from 'components/UI/V2/DropdownSelect/DropdownSelect';
import values from 'lodash/values';
import { WeekDay } from 'model';
import {
    StaffSchedule,
    StaffScheduleCustomDay,
    StaffScheduleByDate,
    StaffSlots,
    StaffSlot,
    StaffCustomDaySlot,
    StaffDefaultSlot
} from 'model/Staff';
import moment, { Moment } from 'moment-timezone';

export enum ScheduleStatus {
    MISSING = 'missing',
    DEFAULT = 'default',
    CUSTOMIZED = 'customized'
}

export const isEmpty = (schedule: StaffSchedule) => {
    return !isCustomized(schedule) && !isDefault(schedule);
};

export const isDefault = (schedule: StaffSchedule) => {
    return values(schedule.default).some(businessHours => businessHours !== null);
};

export const isCustomized = (schedule: StaffSchedule) => {
    return schedule.customDays.length > 0;
};

export const isCustomizedFromThisWeekOn = (schedule: StaffSchedule) => {
    const startOfWeek = moment().startOf('isoWeek');
    const result = schedule.customDays.some(customDay => customDay.from.isSameOrAfter(startOfWeek));
    return result;
};

export const getStatusFromThisWeekOn = (schedule: StaffSchedule) => {
    if (isDefault(schedule) && !isCustomizedFromThisWeekOn(schedule)) {
        return ScheduleStatus.DEFAULT;
    } else if (isCustomizedFromThisWeekOn(schedule)) {
        return ScheduleStatus.CUSTOMIZED;
    } else {
        return ScheduleStatus.MISSING;
    }
};

export const createEmptySchedule = (): StaffSchedule => {
    return {
        default: createEmptyWeek(),
        customDays: []
    };
};

export const removeCustomsEqualsAsDefault = (customDays: Array<StaffScheduleCustomDay>, defaultSchedule: Week) => {
    return customDays.filter(customDay => {
        const weekDay = customDay.from.clone().format('dddd').toLowerCase() as keyof Week;
        const defaultWeekDay = defaultSchedule[weekDay];

        if (!customDay.on && (defaultWeekDay === null || defaultWeekDay === undefined)) {
            return false;
        } else if (customDay.on && defaultWeekDay) {
            return (
                customDay.from.clone().format('H:mm') !== defaultWeekDay.open ||
                customDay.to.clone().format('H:mm') !== defaultWeekDay.close
            );
        } else {
            return true;
        }
    });
};

export const getDefaultScheduleFromDate = (schedule: StaffSchedule, date: Moment | WeekDay) => {
    let weekDay;

    if (typeof date !== 'string') {
        weekDay = date.clone().format('dddd').toLowerCase() as keyof Week;
    } else {
        weekDay = date as keyof Week;
    }

    return schedule.default[weekDay] || null;
};

export const getCustomScheduleFromDate = (schedule: StaffSchedule, date: Moment) => {
    return schedule.customDays.find(day => day.from.isSame(date, 'day'));
};

/**
 * Get the staff schedule for a given date
 * Checks for special hours and default schedule from staff and business
 */
export const getStaffScheduleByDate = (
    marketplace: Marketplace,
    schedule: StaffSchedule,
    date: Moment,
    timeZone: string
): StaffScheduleByDate => {
    const weekDay = date.format('dddd').toLowerCase();

    const staffDefaultSchedule = getDefaultScheduleFromDate(schedule, date);
    const staffSpecialHour = getCustomScheduleFromDate(schedule, date);
    const businessDateSchedule = marketplace.schedule.week[weekDay as keyof Week];
    const businessSpecialHour = marketplace.schedule.specialHours.find(specialHour =>
        moment(specialHour.date).isSame(date, 'day')
    );

    const isStaffAvailable = staffSpecialHour?.on ?? !!staffDefaultSchedule;
    const isBusinessClosed = businessSpecialHour?.isClosed ?? !businessDateSchedule;

    const from =
        staffSpecialHour?.from.clone() ||
        moment(
            staffDefaultSchedule?.open || businessSpecialHour?.businessHours?.open || businessDateSchedule?.open,
            'HH:mm'
        ).tz(timeZone, true);
    const to =
        staffSpecialHour?.to.clone() ||
        moment(
            staffDefaultSchedule?.close || businessSpecialHour?.businessHours?.close || businessDateSchedule?.close,
            'HH:mm'
        ).tz(timeZone, true);

    return {
        from,
        to,
        isBusinessClosed,
        isStaffAvailable
    };
};

/**
 * Get the staff schedule for a given date
 * Checks for special hours and default schedule from staff and business
 */
export const getStaffScheduleByWeekDay = (
    marketplace: Marketplace,
    schedule: StaffSchedule,
    weekDay: WeekDay,
    timeZone: string
): StaffScheduleByDate => {
    const staffDefaultSchedule = getDefaultScheduleFromDate(schedule, weekDay);
    const businessDateSchedule = marketplace.schedule.week[weekDay];

    const isStaffAvailable = !!staffDefaultSchedule;
    const isBusinessClosed = !businessDateSchedule;

    const from = moment(staffDefaultSchedule?.open || businessDateSchedule?.open, 'HH:mm').tz(timeZone, true);
    const to = moment(staffDefaultSchedule?.close || businessDateSchedule?.close, 'HH:mm').tz(timeZone, true);

    return {
        from,
        to,
        isBusinessClosed,
        isStaffAvailable
    };
};

export const getStaffResourceFullCalendarBusinessHours = (
    marketplace: Marketplace,
    schedule: StaffSchedule,
    period: Period,
    timeZone: string
) => {
    const result: Array<{
        daysOfWeek: number[];
        startTime: string;
        endTime: string;
    }> = [];
    const weekdayMap: { [key: string]: number } = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
    };

    for (let date = period.from.clone(); date.isSameOrBefore(period.to, 'day'); date.add(1, 'day')) {
        const weekDay = date.format('dddd').toLowerCase();
        const weekDayNumber = weekdayMap[weekDay];

        const staffScheduleByDate = getStaffScheduleByDate(marketplace, schedule, date, timeZone);

        if (staffScheduleByDate.isStaffAvailable) {
            const startTime = staffScheduleByDate.from.format('HH:mm:ss');
            const endTime = staffScheduleByDate.to.format('HH:mm:ss');

            const existingEntry = result.find(entry => entry.startTime === startTime && entry.endTime === endTime);

            if (existingEntry) {
                existingEntry.daysOfWeek.push(weekDayNumber);
            } else {
                result.push({
                    daysOfWeek: [weekDayNumber],
                    startTime,
                    endTime
                });
            }
        }
    }

    return result;
};

export const isCustomSchedule = (
    schedule: StaffScheduleCustomDay | BusinessHours
): schedule is StaffScheduleCustomDay => {
    return 'from' in schedule && 'to' in schedule;
};

export const isDefaultSchedule = (schedule: StaffScheduleCustomDay | BusinessHours): schedule is BusinessHours => {
    return 'open' in schedule && 'close' in schedule;
};

export const generateHourOptions = (start: Moment, end: Moment) => {
    const hours: Array<DropdownSelectOption<string>> = [];

    while (start.isBefore(end)) {
        hours.push({
            label: start.format('LT'),
            value: start.format('HH:mm')
        });

        start.add(15, 'minutes');
    }

    return hours;
};

export const isCustomSlot = (slot: StaffSlot): slot is StaffCustomDaySlot => {
    return 'date' in slot;
};

export const isDefaultSlot = (slot: StaffSlot): slot is StaffDefaultSlot => {
    return !('date' in slot);
};

export const getStaffSlots = (slots: StaffSlots): Array<StaffSlot> => {
    const weekDaySlots = Object.values(slots.default).flat() as Array<StaffSlot>;
    const customDaySlots = slots.customDays;

    return weekDaySlots.concat(customDaySlots);
};

export const getStaffSlotsByDate = (slots: StaffSlots, date: Moment): Array<StaffSlot> => {
    const weekDay = date.format('dddd').toLowerCase();
    const defaultSlots = slots.default.filter(
        slot => !isDefaultSlotDisabled(slots.customDays, slot, date) && slot.weekDay === weekDay
    );
    const customDaySlots = slots.customDays.filter(slot => slot.on && slot.date.isSame(date, 'day'));

    return [...defaultSlots, ...customDaySlots];
};

export const getStaffSlotsByPeriod = (slots: StaffSlots, period: Period) => {
    const result: Array<StaffSlot> = [];

    for (let date = period.from.clone(); date.isSameOrBefore(period.to, 'day'); date.add(1, 'day')) {
        result.push(...getStaffSlotsByDate(slots, date));
    }

    return result;
};

export const getStaffSlotsPetsCountByDate = (slots: StaffSlots, date: Moment) => {
    return getStaffSlotsByDate(slots, date).reduce((acc, slot) => acc + slot.petsCount, 0);
};

export const getStaffSlotsPetsCountByPeriod = (slots: StaffSlots, period: Period) => {
    let petsCount = 0;

    for (let date = period.from.clone(); date.isSameOrBefore(period.to, 'day'); date.add(1, 'day')) {
        petsCount += getStaffSlotsPetsCountByDate(slots, date);
    }

    return petsCount;
};

export const getDefaultSlotDate = (date: Moment, slot: StaffDefaultSlot) => {
    const [hour, minute] = slot.time.split(':').map(Number);

    const slotDate = date.clone();

    while (slotDate.format('dddd').toLowerCase() !== slot.weekDay) {
        slotDate.add(1, 'day');
    }

    return slotDate.set('hour', hour).set('minute', minute);
};

export const isDefaultSlotDisabled = (slots: Array<StaffSlot>, slot: StaffDefaultSlot, date: Moment) => {
    return slots.some(
        customSlot =>
            isCustomSlot(customSlot) &&
            // customSlot.on === false &&
            customSlot.time === slot.time &&
            customSlot.date.isSame(date, 'day') &&
            customSlot.date.format('dddd').toLowerCase() === slot.weekDay
    );
};
